import { CommunityMark } from "@/components/CommunityMark";
import { ManagedBy } from "@/components/ManagedBy";
import { MessagesSquare, Phone, AlertTriangle, ClipboardList, Users } from "lucide-react";
import { makeVocab, unitWord } from "@/lib/vocab";

// The community card the portfolio console shows (Portfolio Overview + Portfolio → Communities):
// the same face the Redline console gives a community — its logo/brand bar, concierge name, who
// manages it, its line, and live counts that deep-link into the community. `footer` / `children`
// let each page add its own actions or details underneath.
export function CommunityCard({ tenant: t, enterCommunity, pulseChip, testid, children, footer, showStats = true, className = "" }) {
  const vt = makeVocab(t);                 // this card speaks its own community's industry words
  const flagged = (t.stats?.escalations || 0) + (t.stats?.urgent_requests || 0);
  const urgent = t.stats?.urgent_requests || 0;
  const color = t.brand?.color || "#191614";
  const Stat = ({ id, to, title, icon: Icon, value, label, tone }) => (
    <button data-testid={`${testid}-${id}`} onClick={() => enterCommunity(t.id, to)} title={title}
      className={`flex items-center gap-1.5 rounded-full px-2 py-1 -mx-1 transition-colors ${tone === "red" ? "text-redline-600 hover:bg-redline-50" : "text-ink/55 hover:bg-sand-50 hover:text-ink"}`}>
      <Icon className={`h-3.5 w-3.5 ${tone === "red" ? "text-danger-500" : "text-ink/30"}`} strokeWidth={1.75} />
      <span className={`font-bold tabular-nums ${tone === "red" ? "" : "text-ink"}`}>{value}</span> <span className="lowercase">{label}</span>
    </button>
  );
  return (
    <div data-testid={testid}
      className={`group flex flex-col relative h-full bg-white rounded-2xl border border-sand-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_36px_rgba(0,0,0,0.08)] transition-shadow duration-300 overflow-hidden ${className}`}>
      <div className="absolute top-0 inset-x-0 h-1" style={{ backgroundColor: flagged > 0 ? "#e5342e" : color }} data-testid={`${testid}-brand-bar`} />
      <div className="p-4 flex items-start gap-3">
        <button onClick={() => enterCommunity(t.id)} className="shrink-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30" title={`Open ${t.name}`}>
          <CommunityMark tenant={t} size={44} flagged={flagged > 0} testid={`${testid}-mark`} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <button onClick={() => enterCommunity(t.id)} className="text-left flex-1 min-w-0">
              <h3 className="font-heading text-[15px] font-bold text-ink leading-snug line-clamp-2 group-hover:underline decoration-sand-300 underline-offset-2" data-testid={`${testid}-name`}>{t.name}</h3>
            </button>
            {flagged > 0 && <span className="mt-1.5 h-2 w-2 rounded-full bg-redline-500 animate-pulse shrink-0" />}
          </div>
          <div className="text-xs font-semibold truncate mt-0.5" style={{ color: t.brand?.color || "#e5342e" }} data-testid={`${testid}-persona`}>{t.persona_name}</div>
          <ManagedBy tenant={t} testid={`${testid}-manager`} className="mt-1 max-w-full" />
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-medium text-ink/45 tabular-nums">{t.phone_number || "No number yet"}</span>
            {urgent > 0 && (
              <button data-testid={`${testid}-urgent`} onClick={() => enterCommunity(t.id, "/dashboard/actions")} title={`Open urgent ${vt.lcs("request")}`}
                className="rounded-full bg-redline-600 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide hover:bg-redline-700 transition-colors">
                {urgent} urgent
              </button>
            )}
            {(t.stats?.pending_registrations || 0) > 0 && (
              <button data-testid={`${testid}-pending-regs`} onClick={() => enterCommunity(t.id, "/dashboard/roster")} title={`${vt.many("person")} awaiting approval — open roster`}
                className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide hover:bg-amber-200 transition-colors">
                {t.stats.pending_registrations} awaiting approval
              </button>
            )}
            {t.status === "pending_activation" && (
              <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Pending number</span>
            )}
          </div>
        </div>
      </div>
      {showStats ? (
        <div className="px-4 pb-3 flex items-center gap-3 text-xs flex-wrap">
          <Stat id="texts" to="/dashboard/conversations" title="Text conversations (all time)" icon={MessagesSquare} value={t.stats?.texts ?? t.stats?.conversations ?? 0} label="texts" />
          <Stat id="calls" to="/dashboard/conversations?channel=voice" title="Phone calls (all time)" icon={Phone} value={t.stats?.calls ?? 0} label="calls" />
          {flagged > 0 && <Stat id="flagged" to="/dashboard/conversations?status=escalated" title="Open escalated conversations" icon={AlertTriangle} value={flagged} label="flagged" tone="red" />}
          {t.mode !== "answers_only" && <Stat id="requests" to="/dashboard/actions" title="Open requests waiting on the office" icon={ClipboardList} value={t.stats?.open_requests || 0} label="requests" />}
          <Stat id="homes" to="/dashboard/roster" title={`Open ${vt.lcs("person")}`} icon={Users} value={t.stats?.households || 0} label={unitWord(t)} />
          {pulseChip && <span className="ml-auto">{pulseChip}</span>}
        </div>
      ) : pulseChip ? (
        <div className="px-4 pb-3 flex items-center justify-end text-xs" data-testid={`${testid}-pulse-only`}>{pulseChip}</div>
      ) : null}
      {children}
      {footer}
    </div>
  );
}
